// @NOTE we'll probably have to eventually organize eveyrthing in this file.
import type { ClassValue } from 'clsx';
import { clsx } from 'clsx';
import { z } from 'zod';

import { CreditCardEnum } from '@sola/schema/dotcom/checkout/billingInfo.ts';

/**
 * Little utility to check if we are on desktop.
 * Thought it was cleaner to check for desktop than something like isOnDevice?
 */
export const isOnDesktop = () =>
  typeof matchMedia !== 'undefined' &&
  matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * Classname utility where we can swap and add additional utilities.
 */
export const cn = (...inputs: ClassValue[]) => {
  return clsx(inputs);
};

/**
 * Normalize all phone numbers displayed throughout components.
 *
 * This is a very simplistic approach based on immmediate needs for US numbers.
 * Third-party libs seemed to either be poorly maintained or require
 * large imports to handle formatting.
 */
export const formatPhone = (str: string) => {
  const digity = str.replaceAll(/\D/g, '').slice(0, 10);
  return `(${digity.slice(0, 3)}) ${digity.slice(3, 6)}-${digity.slice(6, 10)}`;
};

export const formatCreditCard = (
  cardNumber: string,
  maskChar: string = 'X',
  cardType: CreditCardEnum = CreditCardEnum.NA,
) => {
  const lastFourDigits = cardNumber.slice(-4);
  let ccNumber = cardNumber;
  if (cardNumber.length < 10) {
    ccNumber =
      cardType === CreditCardEnum.Amex
        ? `0000 000000 0${lastFourDigits}`
        : `0000 0000 0000 ${lastFourDigits}`;
  }
  return `${ccNumber.replaceAll(/\d/g, maskChar).slice(0, Math.max(0, ccNumber.length - 4))}${lastFourDigits}`;
};

/**
 * Hide/remove credit card numbers in a string.
 * @param value The string value to search for CC numbers.
 * @param replaceValue The value to replace CC numbers with.
 * @returns A new string without CC values.
 */
export const removeCreditCardNumbers = (value: string, replaceValue = '') => {
  if (!value || value.length < 13) {
    return value;
  }

  const matches = value.match(/(?:\d[ -]*?){13,16}/g);
  if (!matches) {
    return value;
  }

  let newValue = value;
  for (const match of matches) {
    newValue = newValue.replace(match, replaceValue);
  }

  return newValue;
};

const usernameWhitespaceValidator = z.string().regex(/^\s*\S+\s*$/);

export const createUsernameWhitespaceValidator =
  (errorMessage: string) => (value: string) =>
    usernameWhitespaceValidator.safeParse(value).success || errorMessage;

export const PASSWORD_CHECKS = {
  NUMBER: 'number',
  SPECIAL: 'special',
  UPPERCASE: 'uppercase',
  LOWERCASE: 'lowercase',
  LENGTH: 'length',
};

export const passwordValidator = z.string().superRefine((p, ctx) => {
  const passwordLength = p.length >= 8;
  // nosemgrep: nodejs_scan.javascript-dos-rule-regex_dos
  // Character-class repetition over zod-bounded password input. No catastrophic backtracking.
  const specialCharacter = /[\^$*.[\]{}()?\-"!@#%&/,><':;|_~`+=]+/.test(p);
  const number = /\d+/.test(p);
  const uppercase = /[A-Z]+/.test(p);
  const lowercase = /[a-z]+/.test(p);

  if (!passwordLength) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: PASSWORD_CHECKS.LENGTH,
    });
  }
  if (!specialCharacter) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: PASSWORD_CHECKS.SPECIAL,
    });
  }
  if (!number) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: PASSWORD_CHECKS.NUMBER,
    });
  }
  if (!uppercase) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: PASSWORD_CHECKS.UPPERCASE,
    });
  }
  if (!lowercase) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: PASSWORD_CHECKS.LOWERCASE,
    });
  }
});
